import DeleteOutlined from '@ant-design/icons/DeleteOutlined';
import PlusOutlined from '@ant-design/icons/PlusOutlined';
import Alert from 'antd/es/alert';
import Button from 'antd/es/button';
import Card from 'antd/es/card';
import Form from 'antd/es/form';
import Input from 'antd/es/input';
import Select from 'antd/es/select';
import Space from 'antd/es/space';
import Typography from 'antd/es/typography';
import React, { useState } from 'react';
import { styled } from 'styled-components';

import type { PipelineBlock, TransformBlockConfig } from '@mai/types';

// Transform operation types with metadata (from Python implementation)
const TRANSFORM_OPERATIONS = [
  {
    value: 'select',
    label: 'Select Columns',
    description: 'Select specific columns from the data',
  },
  {
    value: 'filter',
    label: 'Filter Rows',
    description: 'Filter rows based on conditions',
  },
  {
    value: 'with_column',
    label: 'Add/Modify Column',
    description: 'Add or modify a single column',
  },
  {
    value: 'with_columns',
    label: 'Add/Modify Multiple Columns',
    description: 'Add or modify multiple columns',
  },
  {
    value: 'drop',
    label: 'Drop Columns',
    description: 'Remove columns from the data',
  },
  { value: 'rename', label: 'Rename Columns', description: 'Rename columns' },
  {
    value: 'cast',
    label: 'Cast Types',
    description: 'Change column data types',
  },
  {
    value: 'explode',
    label: 'Explode Arrays',
    description: 'Expand array columns into rows',
  },
  {
    value: 'implode',
    label: 'Implode to Arrays',
    description: 'Collect values into arrays',
  },
  {
    value: 'json_extract',
    label: 'Extract JSON',
    description: 'Extract data from JSON columns',
  },
  {
    value: 'cross_join',
    label: 'Cross Join',
    description: 'Perform cross join operations',
  },
  {
    value: 'cross_product_external',
    label: 'Cross Product External',
    description: 'External cross product operations',
  },
  {
    value: 'group_by',
    label: 'Group By',
    description: 'Group data by columns',
  },
  {
    value: 'deduplicate_by',
    label: 'Remove Duplicates',
    description: 'Remove duplicate rows',
  },
  {
    value: 'zip_arrays_to_structs',
    label: 'Zip Arrays to Structs',
    description: 'Convert arrays to structures',
  },
  {
    value: 'wrap_in_struct',
    label: 'Wrap in Struct',
    description: 'Wrap data in structures',
  },
  {
    value: 'unnest_struct',
    label: 'Unnest Struct',
    description: 'Flatten structures',
  },
] as const;

const EditorContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.marginXS}px;
`;

const OperationCard = styled(Card)`
  .ant-card-body {
    padding: ${({ theme }) => theme.paddingSM}px;
  }
`;

const HelpText = styled(Typography.Text)`
  color: ${({ theme }) => theme.colorTextSecondary};
  font-size: ${({ theme }) => theme.fontSizeSM}px;
`;

interface TransformBlockEditorProps {
  block: PipelineBlock & { type: 'transform' };
  onConfigChange: (config: TransformBlockConfig) => void;
}

interface ExpressionType {
  type: 'literal' | 'column' | 'binary_op';
  value?: string;
  name?: string;
  operator?: string;
  left?: ExpressionType;
  right?: ExpressionType;
}

interface AggregationType {
  type: 'sum' | 'avg' | 'count' | 'min' | 'max' | 'first' | 'last';
  column?: string;
}

interface TransformOperation {
  type: string;
  columns?: string[] | Record<string, ExpressionType>;
  expr?: ExpressionType;
  alias?: string;
  mapping?: Record<string, string>;
  column?: string;
  path?: string;
  other?: string;
  values?: string[];
  name?: string;
  agg?: Record<string, AggregationType>;
}

// Validation functions
const validateOperation = (operation: TransformOperation): string[] => {
  const errors: string[] = [];

  if (!operation.type) {
    errors.push('Operation type is required');
    return errors;
  }

  switch (operation.type) {
    case 'select':
      if (
        !operation.columns ||
        !Array.isArray(operation.columns) ||
        operation.columns.length === 0
      ) {
        errors.push('Select operation requires at least one column');
      }
      break;

    case 'filter':
      if (!operation.expr) {
        errors.push('Filter operation requires an expression');
      }
      break;

    case 'with_column':
      if (!operation.alias) {
        errors.push('With column operation requires an alias');
      }
      if (!operation.expr) {
        errors.push('With column operation requires an expression');
      }
      break;

    case 'drop':
      if (
        !operation.columns ||
        !Array.isArray(operation.columns) ||
        operation.columns.length === 0
      ) {
        errors.push('Drop operation requires at least one column');
      }
      break;

    case 'rename':
      if (
        !operation.mapping ||
        typeof operation.mapping !== 'object' ||
        Object.keys(operation.mapping).length === 0
      ) {
        errors.push('Rename operation requires column mapping');
      }
      break;

    case 'cast':
      if (
        !operation.mapping ||
        typeof operation.mapping !== 'object' ||
        Object.keys(operation.mapping).length === 0
      ) {
        errors.push('Cast operation requires type mapping');
      }
      break;

    case 'explode':
      if (!operation.column) {
        errors.push('Explode operation requires a column name');
      }
      break;

    case 'group_by':
      if (
        !operation.columns ||
        !Array.isArray(operation.columns) ||
        operation.columns.length === 0
      ) {
        errors.push('Group by operation requires at least one column');
      }
      break;

    case 'implode':
      if (!operation.column) {
        errors.push('Implode operation requires a column name');
      }
      break;

    case 'json_extract':
      if (!operation.column) {
        errors.push('JSON extract operation requires a column name');
      }
      if (!operation.path) {
        errors.push('JSON extract operation requires a path');
      }
      break;

    case 'cross_join':
      if (!operation.other) {
        errors.push('Cross join operation requires other dataset');
      }
      break;

    case 'cross_product_external':
      if (!operation.values) {
        errors.push('Cross product external operation requires values');
      }
      break;

    case 'deduplicate_by':
      if (
        !operation.columns ||
        !Array.isArray(operation.columns) ||
        operation.columns.length === 0
      ) {
        errors.push('Deduplicate by operation requires at least one column');
      }
      break;

    case 'zip_arrays_to_structs':
      if (
        !operation.columns ||
        !Array.isArray(operation.columns) ||
        operation.columns.length === 0
      ) {
        errors.push(
          'Zip arrays to structs operation requires at least one column',
        );
      }
      break;

    case 'wrap_in_struct':
      if (!operation.name) {
        errors.push('Wrap in struct operation requires a struct name');
      }
      break;

    case 'unnest_struct':
      if (!operation.column) {
        errors.push('Unnest struct operation requires a column name');
      }
      break;

    case 'with_columns':
      if (
        !operation.columns ||
        typeof operation.columns !== 'object' ||
        Object.keys(operation.columns).length === 0
      ) {
        errors.push('With columns operation requires column definitions');
      }
      break;
  }

  return errors;
};

// Expression builder component
const ExpressionBuilder: React.FC<{
  value: ExpressionType;
  onChange: (value: ExpressionType) => void;
}> = ({ value, onChange }) => {
  const [exprType, setExprType] = useState<string>(value?.type || 'literal');

  const handleTypeChange = (type: string) => {
    setExprType(type);
    switch (type) {
      case 'literal':
        onChange({ type: 'literal', value: '' });
        break;
      case 'column':
        onChange({ type: 'column', name: '' });
        break;
      case 'binary_op':
        onChange({
          type: 'binary_op',
          operator: '+',
          left: { type: 'column', name: '' },
          right: { type: 'literal', value: '' },
        });
        break;
      default:
        onChange({ type } as ExpressionType);
    }
  };

  const handleValueChange = (updates: Partial<ExpressionType>) => {
    onChange({ ...value, ...updates });
  };

  return (
    <Card size="small" style={{ backgroundColor: '#fafafa' }}>
      <Space direction="vertical" style={{ width: '100%' }}>
        <Form.Item label="Expression Type" style={{ marginBottom: 8 }}>
          <Select
            value={exprType}
            onChange={handleTypeChange}
            style={{ width: '100%' }}
          >
            <Select.Option value="literal">Literal Value</Select.Option>
            <Select.Option value="column">Column Reference</Select.Option>
            <Select.Option value="binary_op">Binary Operation</Select.Option>
          </Select>
        </Form.Item>

        {exprType === 'literal' && (
          <Form.Item label="Value" style={{ marginBottom: 8 }}>
            <Input
              placeholder="Enter literal value"
              value={value?.value || ''}
              onChange={(e) => handleValueChange({ value: e.target.value })}
            />
          </Form.Item>
        )}

        {exprType === 'column' && (
          <Form.Item label="Column Name" style={{ marginBottom: 8 }}>
            <Input
              placeholder="Enter column name"
              value={value?.name || ''}
              onChange={(e) => handleValueChange({ name: e.target.value })}
            />
          </Form.Item>
        )}

        {exprType === 'binary_op' && (
          <>
            <Form.Item label="Operator" style={{ marginBottom: 8 }}>
              <Select
                value={value?.operator || '+'}
                onChange={(operator) => handleValueChange({ operator })}
                style={{ width: '100%' }}
              >
                <Select.Option value="+">+ (Add)</Select.Option>
                <Select.Option value="-">- (Subtract)</Select.Option>
                <Select.Option value="*">* (Multiply)</Select.Option>
                <Select.Option value="/">/ (Divide)</Select.Option>
                <Select.Option value="==">== (Equal)</Select.Option>
                <Select.Option value="!=">!= (Not Equal)</Select.Option>
                <Select.Option value=">">&gt; (Greater Than)</Select.Option>
                <Select.Option value=">=">
                  &gt;= (Greater Than or Equal)
                </Select.Option>
                <Select.Option value="<">&lt; (Less Than)</Select.Option>
                <Select.Option value="<=">
                  &lt;= (Less Than or Equal)
                </Select.Option>
                <Select.Option value="and">AND</Select.Option>
                <Select.Option value="or">OR</Select.Option>
              </Select>
            </Form.Item>

            <Form.Item label="Left Side" style={{ marginBottom: 8 }}>
              <ExpressionBuilder
                value={value?.left || { type: 'column', name: '' }}
                onChange={(left) => handleValueChange({ left })}
              />
            </Form.Item>

            <Form.Item label="Right Side" style={{ marginBottom: 8 }}>
              <ExpressionBuilder
                value={value?.right || { type: 'literal', value: '' }}
                onChange={(right) => handleValueChange({ right })}
              />
            </Form.Item>
          </>
        )}
      </Space>
    </Card>
  );
};

// Column mapping component
const ColumnMappingEditor: React.FC<{
  value: Record<string, string>;
  onChange: (value: Record<string, string>) => void;
  placeholder?: { from: string; to: string };
}> = ({ value, onChange, placeholder }) => {
  const mappings = Object.entries(value || {});

  const addMapping = () => {
    onChange({ ...value, '': '' });
  };

  const removeMapping = (oldName: string) => {
    const newValue = { ...value };
    delete newValue[oldName];
    onChange(newValue);
  };

  const updateMapping = (oldKey: string, newKey: string, newValue: string) => {
    const updated = { ...value };
    delete updated[oldKey];
    updated[newKey] = newValue;
    onChange(updated);
  };

  return (
    <Space direction="vertical" style={{ width: '100%' }}>
      {mappings.map(([from, to], index) => (
        <Space key={index} style={{ width: '100%' }}>
          <Input
            placeholder={placeholder?.from || 'Original name'}
            value={from}
            onChange={(e) => updateMapping(from, e.target.value, to)}
            style={{ flex: 1 }}
          />
          <span>→</span>
          <Input
            placeholder={placeholder?.to || 'New name'}
            value={to}
            onChange={(e) => updateMapping(from, from, e.target.value)}
            style={{ flex: 1 }}
          />
          <Button
            type="text"
            danger
            icon={<DeleteOutlined />}
            onClick={() => removeMapping(from)}
            size="small"
          />
        </Space>
      ))}
      <Button
        type="dashed"
        onClick={addMapping}
        icon={<PlusOutlined />}
        size="small"
      >
        Add Mapping
      </Button>
    </Space>
  );
};

// Aggregation editor component
const AggregationEditor: React.FC<{
  value: Record<string, AggregationType>;
  onChange: (value: Record<string, AggregationType>) => void;
}> = ({ value, onChange }) => {
  const aggregations = Object.entries(value || {});

  const addAggregation = () => {
    onChange({ ...value, '': { type: 'sum', column: '' } });
  };

  const removeAggregation = (name: string) => {
    const newValue = { ...value };
    delete newValue[name];
    onChange(newValue);
  };

  const updateAggregation = (
    oldName: string,
    newName: string,
    aggDef: AggregationType,
  ) => {
    const updated = { ...value };
    delete updated[oldName];
    updated[newName] = aggDef;
    onChange(updated);
  };

  return (
    <Space direction="vertical" style={{ width: '100%' }}>
      {aggregations.map(([name, aggDef], index) => (
        <Card key={index} size="small">
          <Space direction="vertical" style={{ width: '100%' }}>
            <Form.Item label="Aggregation Name" style={{ marginBottom: 8 }}>
              <Input
                placeholder="result_name"
                value={name}
                onChange={(e) =>
                  updateAggregation(name, e.target.value, aggDef)
                }
              />
            </Form.Item>

            <Form.Item label="Aggregation Type" style={{ marginBottom: 8 }}>
              <Select
                value={aggDef?.type || 'sum'}
                onChange={(type) =>
                  updateAggregation(name, name, {
                    ...aggDef,
                    type,
                  } as AggregationType)
                }
                style={{ width: '100%' }}
              >
                <Select.Option value="sum">Sum</Select.Option>
                <Select.Option value="avg">Average</Select.Option>
                <Select.Option value="count">Count</Select.Option>
                <Select.Option value="min">Minimum</Select.Option>
                <Select.Option value="max">Maximum</Select.Option>
                <Select.Option value="first">First</Select.Option>
                <Select.Option value="last">Last</Select.Option>
              </Select>
            </Form.Item>

            {aggDef?.type && aggDef.type !== 'count' && (
              <Form.Item label="Column" style={{ marginBottom: 8 }}>
                <Input
                  placeholder="column_name"
                  value={aggDef?.column || ''}
                  onChange={(e) =>
                    updateAggregation(name, name, {
                      ...aggDef,
                      column: e.target.value,
                    } as AggregationType)
                  }
                />
              </Form.Item>
            )}

            <Button
              type="text"
              danger
              icon={<DeleteOutlined />}
              onClick={() => removeAggregation(name)}
              size="small"
            >
              Remove
            </Button>
          </Space>
        </Card>
      ))}
      <Button
        type="dashed"
        onClick={addAggregation}
        icon={<PlusOutlined />}
        size="small"
      >
        Add Aggregation
      </Button>
    </Space>
  );
};

// Multi-column editor component
const MultiColumnEditor: React.FC<{
  value: Record<string, ExpressionType>;
  onChange: (value: Record<string, ExpressionType>) => void;
}> = ({ value, onChange }) => {
  const columns = Object.entries(value || {});

  const addColumn = () => {
    onChange({ ...value, '': { type: 'literal', value: '' } });
  };

  const removeColumn = (name: string) => {
    const newValue = { ...value };
    delete newValue[name];
    onChange(newValue);
  };

  const updateColumn = (
    oldName: string,
    newName: string,
    expression: ExpressionType,
  ) => {
    const updated = { ...value };
    delete updated[oldName];
    updated[newName] = expression;
    onChange(updated);
  };

  return (
    <Space direction="vertical" style={{ width: '100%' }}>
      {columns.map(([name, expression], index) => (
        <Card key={index} size="small">
          <Space direction="vertical" style={{ width: '100%' }}>
            <Form.Item label="Column Name" style={{ marginBottom: 8 }}>
              <Input
                placeholder="column_name"
                value={name}
                onChange={(e) => updateColumn(name, e.target.value, expression)}
              />
            </Form.Item>

            <Form.Item label="Expression" style={{ marginBottom: 8 }}>
              <ExpressionBuilder
                value={expression}
                onChange={(expr) => updateColumn(name, name, expr)}
              />
            </Form.Item>

            <Button
              type="text"
              danger
              icon={<DeleteOutlined />}
              onClick={() => removeColumn(name)}
              size="small"
            >
              Remove Column
            </Button>
          </Space>
        </Card>
      ))}
      <Button
        type="dashed"
        onClick={addColumn}
        icon={<PlusOutlined />}
        size="small"
      >
        Add Column
      </Button>
    </Space>
  );
};

// Operation-specific form components
const OperationForm: React.FC<{
  operation: TransformOperation;
  index: number;
  onOperationChange: (index: number, operation: TransformOperation) => void;
}> = ({ operation, index, onOperationChange }) => {
  const [validationErrors, setValidationErrors] = useState<string[]>([]);

  const handleChange = (updates: Partial<TransformOperation>) => {
    const newOperation = { ...operation, ...updates };
    onOperationChange(index, newOperation);

    // Validate the operation
    const errors = validateOperation(newOperation);
    setValidationErrors(errors);
  };

  // Validate on mount and when operation changes
  React.useEffect(() => {
    const errors = validateOperation(operation);
    setValidationErrors(errors);
  }, [operation]);

  const renderOperationSpecificFields = () => {
    switch (operation.type) {
      case 'select':
        return (
          <Form.Item label="Columns" help="List of column names to select">
            <Select
              mode="tags"
              placeholder="Enter column names"
              value={operation.columns || []}
              onChange={(columns) => handleChange({ columns })}
              style={{ width: '100%' }}
            />
          </Form.Item>
        );

      case 'filter':
        return (
          <Form.Item label="Filter Expression" help="Expression to filter rows">
            <ExpressionBuilder
              value={
                operation.expr || {
                  type: 'binary_op',
                  operator: '>',
                  left: { type: 'column', name: '' },
                  right: { type: 'literal', value: '' },
                }
              }
              onChange={(expr) => handleChange({ expr })}
            />
          </Form.Item>
        );

      case 'with_column':
        return (
          <Space direction="vertical" style={{ width: '100%' }}>
            <Form.Item
              label="Column Alias"
              help="Name of the new/modified column"
            >
              <Input
                placeholder="column_name"
                value={operation.alias || ''}
                onChange={(e) => handleChange({ alias: e.target.value })}
              />
            </Form.Item>
            <Form.Item
              label="Expression"
              help="Expression to compute column value"
            >
              <ExpressionBuilder
                value={
                  operation.expr || {
                    type: 'binary_op',
                    operator: '*',
                    left: { type: 'column', name: 'quantity' },
                    right: { type: 'column', name: 'price' },
                  }
                }
                onChange={(expr) => handleChange({ expr })}
              />
            </Form.Item>
          </Space>
        );

      case 'drop':
        return (
          <Form.Item
            label="Columns to Drop"
            help="List of column names to remove"
          >
            <Select
              mode="tags"
              placeholder="Enter column names"
              value={operation.columns || []}
              onChange={(columns) => handleChange({ columns })}
              style={{ width: '100%' }}
            />
          </Form.Item>
        );

      case 'rename':
        return (
          <Form.Item
            label="Column Mapping"
            help="Map old column names to new names"
          >
            <ColumnMappingEditor
              value={operation.mapping || {}}
              onChange={(mapping) => handleChange({ mapping })}
              placeholder={{ from: 'old_name', to: 'new_name' }}
            />
          </Form.Item>
        );

      case 'cast':
        return (
          <Form.Item
            label="Type Mapping"
            help="Map column names to new data types"
          >
            <ColumnMappingEditor
              value={operation.mapping || {}}
              onChange={(mapping) => handleChange({ mapping })}
              placeholder={{ from: 'column_name', to: 'int' }}
            />
          </Form.Item>
        );

      case 'explode':
        return (
          <Form.Item label="Column" help="Name of the array column to explode">
            <Input
              placeholder="array_column"
              value={operation.column || ''}
              onChange={(e) => handleChange({ column: e.target.value })}
            />
          </Form.Item>
        );

      case 'group_by':
        return (
          <Space direction="vertical" style={{ width: '100%' }}>
            <Form.Item label="Group By Columns" help="Columns to group by">
              <Select
                mode="tags"
                placeholder="Enter column names"
                value={operation.columns || []}
                onChange={(columns) => handleChange({ columns })}
                style={{ width: '100%' }}
              />
            </Form.Item>
            <Form.Item label="Aggregations" help="Aggregation operations">
              <AggregationEditor
                value={operation.agg || {}}
                onChange={(agg) => handleChange({ agg })}
              />
            </Form.Item>
          </Space>
        );

      case 'implode':
        return (
          <Form.Item
            label="Column"
            help="Name of the column to collect into arrays"
          >
            <Input
              placeholder="column_name"
              value={operation.column || ''}
              onChange={(e) => handleChange({ column: e.target.value })}
            />
          </Form.Item>
        );

      case 'json_extract':
        return (
          <Space direction="vertical" style={{ width: '100%' }}>
            <Form.Item label="Column" help="Name of the JSON column">
              <Input
                placeholder="json_column"
                value={operation.column || ''}
                onChange={(e) => handleChange({ column: e.target.value })}
              />
            </Form.Item>
            <Form.Item label="Path" help="JSON path to extract">
              <Input
                placeholder="$.field.subfield"
                value={operation.path || ''}
                onChange={(e) => handleChange({ path: e.target.value })}
              />
            </Form.Item>
            <Form.Item label="Alias" help="Name for the extracted column">
              <Input
                placeholder="extracted_value"
                value={operation.alias || ''}
                onChange={(e) => handleChange({ alias: e.target.value })}
              />
            </Form.Item>
          </Space>
        );

      case 'cross_join':
        return (
          <Form.Item
            label="Other Dataset"
            help="Name of the other dataset to cross join with"
          >
            <Input
              placeholder="dataset_name"
              value={operation.other || ''}
              onChange={(e) => handleChange({ other: e.target.value })}
            />
          </Form.Item>
        );

      case 'cross_product_external':
        return (
          <Form.Item label="Values" help="External values for cross product">
            <Select
              mode="tags"
              placeholder="Enter values (press Enter to add)"
              value={operation.values || []}
              onChange={(values) => handleChange({ values })}
              style={{ width: '100%' }}
            />
          </Form.Item>
        );

      case 'deduplicate_by':
        return (
          <Form.Item label="Columns" help="Columns to use for deduplication">
            <Select
              mode="tags"
              placeholder="Enter column names"
              value={operation.columns || []}
              onChange={(columns) => handleChange({ columns })}
              style={{ width: '100%' }}
            />
          </Form.Item>
        );

      case 'zip_arrays_to_structs':
        return (
          <Form.Item
            label="Array Columns"
            help="Array columns to zip into structs"
          >
            <Select
              mode="tags"
              placeholder="Enter array column names"
              value={operation.columns || []}
              onChange={(columns) => handleChange({ columns })}
              style={{ width: '100%' }}
            />
          </Form.Item>
        );

      case 'wrap_in_struct':
        return (
          <Space direction="vertical" style={{ width: '100%' }}>
            <Form.Item label="Struct Name" help="Name of the struct column">
              <Input
                placeholder="struct_name"
                value={operation.name || ''}
                onChange={(e) => handleChange({ name: e.target.value })}
              />
            </Form.Item>
            <Form.Item
              label="Columns"
              help="Columns to wrap in struct (optional)"
            >
              <Select
                mode="tags"
                placeholder="Enter column names (leave empty for all columns)"
                value={operation.columns || []}
                onChange={(columns) => handleChange({ columns })}
                style={{ width: '100%' }}
              />
            </Form.Item>
          </Space>
        );

      case 'unnest_struct':
        return (
          <Form.Item
            label="Struct Column"
            help="Name of the struct column to unnest"
          >
            <Input
              placeholder="struct_column"
              value={operation.column || ''}
              onChange={(e) => handleChange({ column: e.target.value })}
            />
          </Form.Item>
        );

      case 'with_columns':
        return (
          <Form.Item
            label="Column Definitions"
            help="Define multiple columns with expressions"
          >
            <MultiColumnEditor
              value={
                (operation.columns as Record<string, ExpressionType>) || {}
              }
              onChange={(columns) => handleChange({ columns })}
            />
          </Form.Item>
        );

      default:
        return (
          <Alert
            type="info"
            message="Operation Not Supported"
            description={`The operation type "${operation.type}" is not supported in the visual editor. Please select a different operation type from the dropdown above.`}
            showIcon
          />
        );
    }
  };

  return (
    <Space direction="vertical" style={{ width: '100%' }}>
      <Form.Item label="Operation Type">
        <Select
          value={operation.type}
          onChange={(type) => handleChange({ type })}
          style={{ width: '100%' }}
        >
          {TRANSFORM_OPERATIONS.map((op) => (
            <Select.Option
              key={op.value}
              value={op.value}
              title={op.description}
            >
              {op.label}
            </Select.Option>
          ))}
        </Select>
      </Form.Item>
      {renderOperationSpecificFields()}

      {validationErrors.length > 0 && (
        <Alert
          type="error"
          message="Operation Configuration Issues"
          description={
            <ul style={{ margin: 0, paddingLeft: '20px' }}>
              {validationErrors.map((error, idx) => (
                <li key={idx}>{error}</li>
              ))}
            </ul>
          }
          showIcon
        />
      )}
    </Space>
  );
};

export const TransformBlockEditor: React.FC<TransformBlockEditorProps> = ({
  block,
  onConfigChange,
}) => {
  const [operations, setOperations] = useState<TransformOperation[]>(() => {
    // Initialize with existing operations or empty array
    return (block.config.operations as unknown as TransformOperation[]) || [];
  });

  // Update operations when block config changes
  React.useEffect(() => {
    setOperations(
      (block.config.operations as unknown as TransformOperation[]) || [],
    );
  }, [block.config.operations]);

  const handleOperationChange = (
    index: number,
    operation: TransformOperation,
  ) => {
    const newOperations = [...operations];
    newOperations[index] = operation;
    setOperations(newOperations);

    // Update the block configuration
    onConfigChange({
      ...block.config,
      operations: newOperations as unknown as Record<string, unknown>[],
    });
  };

  const addOperation = () => {
    const newOperation: TransformOperation = { type: 'select' };
    const newOperations = [...operations, newOperation];
    setOperations(newOperations);

    onConfigChange({
      ...block.config,
      operations: newOperations as unknown as Record<string, unknown>[],
    });
  };

  const removeOperation = (index: number) => {
    const newOperations = operations.filter((_, i) => i !== index);
    setOperations(newOperations);

    onConfigChange({
      ...block.config,
      operations: newOperations as unknown as Record<string, unknown>[],
    });
  };

  return (
    <EditorContainer>
      <Space direction="vertical" style={{ width: '100%' }}>
        <Typography.Title level={5} style={{ margin: 0 }}>
          Transform Operations
        </Typography.Title>
        <HelpText>
          Configure transformation operations to apply to the input data.
          Operations are applied in sequence.
        </HelpText>

        {operations.map((operation, index) => (
          <OperationCard key={index} size="small">
            <Space direction="vertical" style={{ width: '100%' }}>
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                }}
              >
                <Typography.Text strong>Operation {index + 1}</Typography.Text>
                <Button
                  type="text"
                  danger
                  icon={<DeleteOutlined />}
                  onClick={() => removeOperation(index)}
                  size="small"
                >
                  Remove
                </Button>
              </div>

              <OperationForm
                operation={operation}
                index={index}
                onOperationChange={handleOperationChange}
              />
            </Space>
          </OperationCard>
        ))}

        <Button
          type="dashed"
          onClick={addOperation}
          icon={<PlusOutlined />}
          style={{ width: '100%' }}
        >
          Add Operation
        </Button>
      </Space>
    </EditorContainer>
  );
};
